import React, { useState, useEffect } from 'react';
import { governanceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Download, 
  Eye,
  Search,
  Shield,
  Lock,
  AlertTriangle,
  Heart,
  Globe,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const Governance = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await governanceAPI.getDocuments();
      setDocuments(response.data);
    } catch (error) {
      console.error('Governance fetch error:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Ethics': return BookOpen;
      case 'Privacy': return Lock;
      case 'Compliance': return AlertTriangle;
      case 'Security': return Shield;
      case 'Safety': return Heart;
      case 'Operations': return Globe;
      default: return FileText;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Ethics': return 'bg-blue-100 text-blue-700';
      case 'Privacy': return 'bg-violet-100 text-violet-700';
      case 'Compliance': return 'bg-amber-100 text-amber-700';
      case 'Security': return 'bg-red-100 text-red-700';
      case 'Safety': return 'bg-emerald-100 text-emerald-700';
      case 'Operations': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filteredDocs = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleView = (doc) => {
    toast.success(`Opening ${doc.title}...`);
  };

  const handleDownload = (doc) => {
    toast.success(`Downloading ${doc.title}...`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="governance-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Governance & Policies
              </h2>
              <p className="text-slate-300">
                Company policies, procedures and compliance documents
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="governance-search"
        />
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocs.map((doc) => {
          const Icon = getCategoryIcon(doc.category);
          return (
            <Card key={doc.id} className="card-hover" data-testid={`doc-card-${doc.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getCategoryColor(doc.category).replace('text-', 'bg-').split(' ')[0]}`}>
                    <Icon className={`w-6 h-6 ${getCategoryColor(doc.category).split(' ')[1]}`} />
                  </div>
                  <Badge className={getCategoryColor(doc.category)}>
                    {doc.category}
                  </Badge>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {doc.title}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {doc.description}
                </p>
                
                <div className="flex items-center justify-between text-xs text-slate-400 mb-4">
                  <span>Version {doc.version}</span>
                  <span>Updated: {doc.last_updated}</span>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleView(doc)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredDocs.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Documents Found</h3>
          <p className="text-slate-500">Try adjusting your search</p>
        </div>
      )}
    </div>
  );
};

export default Governance;
